import { forwardRef, useEffect, useRef } from 'react'
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { controlClass, describeError } from '../lib/forms'

/**
 * Form controls in the §3 vocabulary.
 *
 * One definition per control, so a focus ring or an error border is the same
 * everywhere. The sign-in form's `inputClass` was the first of these; it is kept
 * compatible rather than duplicated.
 */

/**
 * A labelled control with its hint and error.
 *
 * The error is tied to the control with aria-describedby, so a screen reader
 * announces why a field was refused rather than only that it was.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
  className = '',
}: {
  label: string
  htmlFor: string
  hint?: ReactNode
  error?: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-dpl-muted">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} className="mt-1 text-xs text-dpl-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${htmlFor}-hint`} className="mt-1 text-xs text-dpl-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

type WithError = { invalid?: boolean }

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & WithError>(
  function TextInput({ invalid, className = '', ...props }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        aria-describedby={props.id ? `${props.id}-${invalid ? 'error' : 'hint'}` : undefined}
        className={`${controlClass(invalid, className)} ${className}`}
        {...props}
      />
    )
  },
)

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & WithError>(
  function Select({ invalid, className = '', children, ...props }, ref) {
    return (
      <select ref={ref} aria-invalid={invalid || undefined} className={`${controlClass(invalid, className)} ${className}`} {...props}>
        {children}
      </select>
    )
  },
)

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & WithError>(
  function TextArea({ invalid, className = '', rows = 4, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={invalid || undefined}
        className={`${controlClass(invalid, className)} ${className}`}
        {...props}
      />
    )
  },
)

type Tone = 'primary' | 'secondary' | 'danger' | 'ghost'

const tones: Record<Tone, string> = {
  primary: 'bg-dpl-primary text-white hover:bg-dpl-primary-hover',
  secondary: 'border border-dpl-border bg-dpl-surface text-dpl-text hover:border-dpl-primary/50',
  danger: 'bg-dpl-danger text-white hover:bg-dpl-danger/90',
  ghost: 'text-dpl-primary hover:bg-dpl-bg',
}

export function Button({
  tone = 'primary',
  busy = false,
  className = '',
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone; busy?: boolean }) {
  return (
    <button
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-60 ${tones[tone]} ${className}`}
      {...props}
    >
      {busy && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />}
      {children}
    </button>
  )
}

type BadgeTone = 'neutral' | 'success' | 'danger' | 'warning' | 'info'

const badgeTones: Record<BadgeTone, string> = {
  neutral: 'bg-dpl-bg text-dpl-muted',
  success: 'bg-dpl-success/10 text-dpl-success',
  danger: 'bg-dpl-danger/10 text-dpl-danger',
  warning: 'bg-dpl-accent/15 text-[#8A5A00]',
  info: 'bg-dpl-primary/10 text-dpl-primary',
}

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeTones[tone]}`}>
      {children}
    </span>
  )
}

export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null

  return (
    <div role="alert" className="rounded-md border border-dpl-danger/30 bg-dpl-danger/5 px-3 py-2 text-sm text-dpl-danger">
      {describeError(error)}
    </div>
  )
}

/**
 * A modal dialogue on the native <dialog> element.
 *
 * Native because it gets focus trapping, Escape to close and the inert backdrop
 * for free — the three things hand-rolled modals most often get wrong, and all
 * three matter at a till run from a keyboard.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal?.()
    if (!open && dialog.open) dialog.close?.()
  }, [open])

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby="modal-title"
      className={`w-[calc(100%-2rem)] ${wide ? 'max-w-3xl' : 'max-w-lg'} rounded-lg border border-dpl-border bg-dpl-surface p-0 text-dpl-text shadow-xl backdrop:bg-black/40`}
    >
      {open && (
        <>
          <header className="flex items-center justify-between gap-3 border-b border-dpl-border px-4 py-3.5 sm:px-5">
            <h2 id="modal-title" className="text-sm font-semibold">
              {title}
            </h2>
            <button type="button" onClick={onClose} aria-label="Close" className="rounded px-2 text-dpl-muted hover:bg-dpl-bg">
              ×
            </button>
          </header>
          <div className="px-4 py-4 sm:px-5">{children}</div>
          {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-dpl-border px-4 py-3 sm:px-5">{footer}</footer>}
        </>
      )}
    </dialog>
  )
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number
  pageSize: number
  total: number
  onPage: (page: number) => void
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  if (total <= pageSize) return null

  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  return (
    <nav aria-label="Pagination" className="flex items-center justify-between gap-3 text-sm text-dpl-muted">
      <span>
        {from.toLocaleString('en-KE')}–{to.toLocaleString('en-KE')} of {total.toLocaleString('en-KE')}
      </span>
      <div className="flex gap-2">
        <Button tone="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <Button tone="secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </nav>
  )
}
